import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TppEventSummary } from '../../../services/transitPassPool.service';
import { displayTppPurpose } from '../../../utils/tppPurposeDisplay';
import {
  displayTppEventKind,
  formatHistoryAmount,
  formatHistoryDate,
  tppEventNeedsProofUi,
} from './tppHistoryFormat';
import TppProofUploadButton from './TppProofUploadButton';

type TppHistoryMobileCardsProps = {
  events: TppEventSummary[];
  useJaDates: boolean;
  amountPrefix: string;
  amountSuffix: string;
  formatAbsAmount: (n: number) => string;
  onEditEvent: (ev: TppEventSummary) => void;
  onUploadProof: (eventId: number, file: File) => void;
  uploadProofPending: boolean;
  uploadProofEventId?: number;
  uploadProofError: boolean;
  onViewProof: (eventId: number) => void;
  proofViewerLoading: boolean;
};

export default function TppHistoryMobileCards({
  events,
  useJaDates,
  amountPrefix,
  amountSuffix,
  formatAbsAmount,
  onEditEvent,
  onUploadProof,
  uploadProofPending,
  uploadProofEventId,
  uploadProofError,
  onViewProof,
  proofViewerLoading,
}: TppHistoryMobileCardsProps) {
  const { t } = useTranslation();

  return (
    <div className="p-2">
      <div className="d-flex flex-column gap-2">
        {events.map((ev) => {
          const amt = formatHistoryAmount(ev, amountPrefix, amountSuffix, formatAbsAmount);
          const purpose = displayTppPurpose(ev.eventKind, ev.purpose, t);
          const inDateLabel =
            ev.eventKind === 'charge'
              ? null
              : !ev.inDate
                ? t('tpp.history_not_returned')
                : formatHistoryDate(ev.inDate, useJaDates);
          return (
            <div key={ev.id} className="rounded border bg-white p-2">
              <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                <div className="min-w-0">
                  <span className="fw-semibold me-2">{displayTppEventKind(ev, t)}</span>
                  <span className="small text-muted text-nowrap">
                    {formatHistoryDate(ev.outDate, useJaDates)}
                  </span>
                  {inDateLabel ? (
                    <span
                      className={`small text-nowrap ${
                        !ev.inDate ? 'text-warning-emphasis' : 'text-muted'
                      }`}
                    >
                      {' '}
                      → {inDateLabel}
                    </span>
                  ) : null}
                </div>
                <span className={`fw-semibold text-nowrap ${amt.className}`}>{amt.text}</span>
              </div>
              {purpose ? <div className="small mb-1 text-break">{purpose}</div> : null}
              {ev.route?.trim() ? (
                <div className="small text-muted text-break mb-2">{ev.route}</div>
              ) : null}
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {tppEventNeedsProofUi(ev) && ev.hasProof ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-primary"
                    disabled={proofViewerLoading}
                    onClick={() => onViewProof(ev.id)}
                  >
                    {t('tpp.view_proof')}
                  </Button>
                ) : null}
                {tppEventNeedsProofUi(ev) ? (
                  <TppProofUploadButton
                    hasProof={ev.hasProof}
                    pending={uploadProofPending && uploadProofEventId === ev.id}
                    label={t('tpp.upload_proof_short')}
                    replaceLabel={t('tpp.replace_proof_short')}
                    onFile={(f) => onUploadProof(ev.id, f)}
                  />
                ) : null}
                <Button size="sm" variant="outline-primary" onClick={() => onEditEvent(ev)}>
                  {t('common.edit')}
                </Button>
                {uploadProofError && uploadProofEventId === ev.id && (
                  <span className="text-danger small">{t('tpp.proof_upload_failed')}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
